import type { ReactNode, RefObject } from 'react';
import { useMemo } from 'react';

import type { StickySectionEntry } from '#/components/ui/StickySectionNav';
import { StickySectionNav } from '#/components/ui/StickySectionNav';
import { useSectionNavigation } from '#/hooks/useSectionNavigation';

interface SectionNavProps {
  sections: StickySectionEntry[];
  /** Attached by the page to the content column the nav scrolls and spies on. */
  contentRef: RefObject<HTMLDivElement | null>;
  /**
   * Optional row docked above the section list (the detail pages' mobile
   * condensed summary). Category pages render the nav without one.
   */
  condensedSummary?: ReactNode;
  /**
   * Optional GTM annotation forwarded to the section jump buttons. Omitted
   * leaves them to generic auto-capture (detail page unchanged).
   */
  jumpTrack?: { track: string; context: string };
}

/**
 * Owns the scroll-driven section-nav state — the scroll-spy `activeSection`,
 * jump-link scrolling, and which of `sections` the page actually rendered —
 * and renders the presentational StickySectionNav.
 *
 * The scroll-spy hook lives here, in a component that renders only the nav,
 * so its `activeSection` updates — which fire on every section crossing while
 * scrolling — re-render only this subtree, not the page's sections.
 */
export function SectionNav({
  sections,
  contentRef,
  condensedSummary,
  jumpTrack,
}: SectionNavProps) {
  const sectionIds = useMemo(() => sections.map(({ id }) => id), [sections]);
  const { activeSection, onSelect, skipToContent, presentSectionIds } =
    useSectionNavigation(sectionIds, contentRef);

  // An entry for a section that never rendered is a link that scrolls nowhere.
  const visibleSections = useMemo(
    () => sections.filter(({ id }) => presentSectionIds.includes(id)),
    [sections, presentSectionIds],
  );

  return (
    <StickySectionNav
      sections={visibleSections}
      onSelect={onSelect}
      activeSection={activeSection}
      onSkip={skipToContent}
      condensedSummary={condensedSummary}
      jumpTrack={jumpTrack}
    />
  );
}
